import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '../../../store/app'
import { sendRelayInput, sendRelayFile, wsRequest } from '../../../api/ws'
import { bridge } from '../../../bridge'
import './InputBar.css'

type Member = {
  id: string
  name: string
  email?: string
  // 'agent' is the conversation's AI participant — included in the picker
  // for UX (so users can address it explicitly: "@Claude, ...") but
  // intentionally NOT serialized into the wire `mentions` array, since
  // that field exists for routing to humans (validation, future push
  // fan-out). The agent reads its own name out of the body just like a
  // human reading "@Bob, ..." would.
  kind?: 'human' | 'agent'
}

// Unique suffix for an optimistic entry's local id and its client_request_id.
// Not cryptographic — it only has to be collision-free within a session so the
// relay's ack can find the row it belongs to.
function localID(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
}

// Mention token format: `@Display_Name`. Display names get their spaces
// converted to underscores so a single whitespace-bounded token uniquely
// identifies the picked user. The map below remembers which token maps
// back to which user id; on send we filter to tokens still present in
// the textarea so backspacing a mention drops it from the wire payload.
function mentionToken(name: string): string {
  return '@' + name.replace(/\s+/g, '_')
}

type MicState =
  | 'hidden'           // Not supported on this device/locale
  | 'idle'             // Ready to record
  | 'needs-download'   // Supported, but model not on disk yet
  | 'downloading'      // Model downloading (triggered by us)
  | 'loading'          // Model on disk; JIT-compile/warmup in progress
  | 'recording'        // Active recording
  | 'transcribing'     // Post-stop, waiting for text

/// Bottom input bar for sending text to the focused agent. Mic button is
/// device/locale-gated + model-download-gated, all driven by
/// whisper.isAvailable's shape from the native side. The in-browser mock
/// bridge returns supported=false so the button hides entirely.
///
/// Voice input is NOT entitlement-gated: whisper.isAvailable answers a
/// capability question only (can this device and locale run it), never a
/// subscription one. See docs/voice-ungating-plan.md.
export function InputBar({ agentInstanceID }: { agentInstanceID: string }) {
  const text = useAppStore((s) => s.relayInputDrafts[agentInstanceID] ?? '')
  const setDraft = useAppStore((s) => s.setRelayInputDraft)
  const setText = (t: string) => setDraft(agentInstanceID, t)
  // A voice session is open on THIS agent, so dictated messages should be sent
  // as spoken turns and answered aloud. See VoiceSessionBar.
  const voiceActive =
    useAppStore((s) => s.voiceSessionAgentID) === agentInstanceID
  const agentName = useAppStore(
    (s) => s.agentInstances.find((a) => a.id === agentInstanceID)?.name,
  )
  const organizationID = useAppStore((s) => s.organizationID)
  const humanUserID = useAppStore((s) => s.humanUserID)
  const appendTranscript = useAppStore((s) => s.appendTranscript)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [micState, setMicState] = useState<MicState>('hidden')
  const [uploading, setUploading] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)
  // Set when a message couldn't be handed to an OPEN socket. We restore the
  // text and show this instead of clearing the composer, so a message to the
  // agent can't vanish silently on a flaky connection.
  const [sendError, setSendError] = useState(false)

  // Org member roster for the @-picker. Lazy-loaded on first relevant
  // keystroke so scenes with no mention activity don't pay the round-trip.
  const [members, setMembers] = useState<Member[] | null>(null)
  const [memberLoading, setMemberLoading] = useState(false)

  // Token -> user id map. We store the canonical token (after the
  // space-stripping in mentionToken) so the lookup matches what's
  // actually in the textarea. Multiple distinct picks of the same user
  // collapse here and dedupe on send.
  const mentionsRef = useRef<Map<string, Member>>(new Map())

  // Mention popover state. `query` is the partial after the active `@`
  // (excluding the @ itself). `anchor` is the start index of the `@` so
  // we know what range to replace on selection.
  const [mentionState, setMentionState] = useState<
    | null
    | { anchor: number; query: string }
  >(null)
  const [mentionHighlight, setMentionHighlight] = useState(0)

  async function refreshMic() {
    try {
      const r = await bridge.call('whisper.isAvailable')
      if (!r.supported) {
        setMicState('hidden')
        return
      }
      if (r.ready) setMicState('idle')
      else if (r.downloading) setMicState('downloading')
      else if (r.loading) setMicState('loading')
      else if (r.needsDownload) setMicState('needs-download')
      else setMicState('loading') // safest fallback — kicks ensureReady
    } catch {
      setMicState('hidden')
    }
  }

  // Mount-only: availability is a device/locale/model fact, not a per-render
  // one. It used to re-run when the household's Pro status changed; nothing
  // about voice depends on entitlement any more.
  useEffect(() => {
    void refreshMic()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const max = 24 * 6 // ~6 lines
    el.style.height = `${Math.min(max, Math.max(24, el.scrollHeight))}px`
  }, [text])

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!e.target) return
    // Reset so the same file can be re-selected after an error.
    ;(e.target as HTMLInputElement).value = ''
    if (!file) return

    setAttachError(null)
    setUploading(true)
    try {
      const dataURL = await new Promise<string>((res, rej) => {
        const reader = new FileReader()
        reader.onload = () => res(reader.result as string)
        reader.onerror = () => rej(reader.error)
        reader.readAsDataURL(file)
      })
      // Strip the "data:<mime>;base64," prefix to get raw base64.
      const dataB64 = dataURL.slice(dataURL.indexOf(',') + 1)
      // Check encoded size — base64 is ~33% larger than raw; server limit is 12 MB.
      if (dataB64.length > 8 * 1024 * 1024) {
        setAttachError('File too large — max ~6 MB')
        return
      }
      await sendRelayFile({ agentInstanceID }, file.name, file.type || 'application/octet-stream', dataB64)
      // Append a note to the draft so the agent sees what landed.
      const note = `📎 Added: \`${file.name}\``
      setText(text ? `${text}\n${note}` : note)
      bridge.call('haptic.success').catch(() => {})
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function send() {
    const trimmed = text.trim()
    if (!trimmed) return
    // Filter remembered mentions to ones whose token still appears in
    // the body (user may have backspaced one out). Send only id/name —
    // server validates membership against the agent's org.
    const surviving: { id: string; name: string }[] = []
    const seen = new Set<string>()
    for (const [token, m] of mentionsRef.current) {
      if (!trimmed.includes(token)) continue
      if (seen.has(m.id)) continue
      seen.add(m.id)
      surviving.push({ id: m.id, name: m.name })
    }
    const reqID = localID()
    const delivered = sendRelayInput(
      agentInstanceID,
      trimmed,
      surviving.length > 0 ? surviving : undefined,
      reqID,
    )
    if (!delivered) {
      // Socket wasn't OPEN — keep the text (don't clear) and surface the
      // failure. The heartbeat reconnects fast, so a retry lands quickly.
      setSendError(true)
      return
    }
    // Show the message straight away, marked as not yet received. The agent may
    // be mid-turn, in which case the daemon holds it until the turn ends — with
    // nothing rendered, the sender would stare at an empty transcript for the
    // length of that turn and reasonably conclude their message was lost.
    // relay_input_ack attaches the mid; the real turn then displaces this row.
    appendTranscript([
      {
        id: 'local-' + reqID,
        ai_agent_instance_id: agentInstanceID,
        type: 'queued_user',
        text: trimmed,
        tool_name: null,
        tool_input: null,
        timestamp: Date.now(),
        // Sort to the bottom until the real entry arrives. A number (rather
        // than undefined) keeps the bucket fully stamped, which is what lets
        // the store order by seq at all — one un-stamped entry drops the whole
        // bucket back to arrival order.
        seq: Number.MAX_SAFE_INTEGER,
        sub: 0,
        block: 0,
        send_state: 'sending',
      },
    ])
    setSendError(false)
    setText('')
    mentionsRef.current.clear()
    setMentionState(null)
  }

  // Load org roster on demand. Excludes the caller — no point @-mentioning
  // yourself. Cached for the lifetime of the InputBar instance.
  async function ensureMembers(): Promise<Member[] | null> {
    if (members !== null) return members
    if (!organizationID) return null
    if (memberLoading) return null
    setMemberLoading(true)
    try {
      const r = await wsRequest<{ human_users?: Member[] }>(
        'list_human_users',
        { organization_id: organizationID },
        'human_users',
        { kind: 'organization', id: organizationID, action: 'list_human_users' },
      )
      const filtered = (r.human_users ?? []).filter(
        (m) => m.id !== humanUserID && (m.name || m.email),
      )
      // Fall back to email when name is empty so the picker still has a label.
      const normalized = filtered.map((m) => ({
        ...m,
        name: m.name || m.email || m.id,
      }))
      setMembers(normalized)
      return normalized
    } catch {
      setMembers([])
      return []
    } finally {
      setMemberLoading(false)
    }
  }

  // Recompute the mention popover state from the current textarea value
  // and caret position. Triggered on every keystroke. The popover opens
  // when the caret sits inside an `@partial` token (no whitespace between
  // `@` and the caret); typing a space or moving the caret away closes it.
  function recomputeMentionState(value: string, caret: number) {
    // Walk back from the caret to find an `@` not preceded by alphanumeric.
    let i = caret - 1
    while (i >= 0) {
      const ch = value[i]
      if (ch === '@') break
      if (/\s/.test(ch)) {
        setMentionState(null)
        return
      }
      i -= 1
    }
    if (i < 0) {
      setMentionState(null)
      return
    }
    // The `@` must be at the start of input or follow whitespace —
    // otherwise it's an email or similar, not a mention trigger.
    if (i > 0 && !/\s/.test(value[i - 1])) {
      setMentionState(null)
      return
    }
    setMentionState({ anchor: i, query: value.slice(i + 1, caret) })
    setMentionHighlight(0)
    void ensureMembers()
  }

  // The agent itself sits at the top of the picker so addressing it
  // explicitly (`@Claude, …`) is one keystroke away. It piggy-backs on
  // the Member shape but is filtered out of the wire mentions on send.
  const pickerMembers = useMemo(() => {
    const list: Member[] = []
    if (agentName) {
      list.push({
        id: agentInstanceID,
        name: agentName,
        kind: 'agent',
      })
    }
    if (members) list.push(...members)
    return list
  }, [agentName, agentInstanceID, members])

  const filteredMembers = useMemo(() => {
    if (!mentionState) return []
    const q = mentionState.query.toLowerCase()
    if (!q) return pickerMembers.slice(0, 8)
    return pickerMembers
      .filter((m) => m.name.toLowerCase().includes(q))
      .slice(0, 8)
  }, [mentionState, pickerMembers])

  function pickMention(member: Member) {
    if (!mentionState) return
    const el = textareaRef.current
    if (!el) return
    const token = mentionToken(member.name)
    const before = text.slice(0, mentionState.anchor)
    const after = text.slice(mentionState.anchor + 1 + mentionState.query.length)
    // Insert a trailing space so the user can keep typing without manually
    // breaking out of the mention.
    const next = before + token + ' ' + after
    setText(next)
    // Only humans go into the wire `mentions` array; the agent reads its
    // own name from the body text directly.
    if (member.kind !== 'agent') {
      mentionsRef.current.set(token, member)
    }
    setMentionState(null)
    // Restore caret position right after the inserted token + space.
    const newCaret = before.length + token.length + 1
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(newCaret, newCaret)
    })
  }

  async function onMic() {
    if (micState === 'needs-download') {
      if (!confirm('Download voice model? About 39 MB, one-time download.')) {
        return
      }
      setMicState('downloading')
      try {
        const r = await bridge.call('whisper.prepare')
        if (!r.ready) {
          alert(`Couldn't download voice model${r.error ? ': ' + r.error : ''}`)
          setMicState('needs-download')
          return
        }
        setMicState('idle')
        // Fall through to starting recording so the user doesn't have to
        // tap twice after waiting for the download.
      } catch (err) {
        alert(`Voice setup failed: ${err instanceof Error ? err.message : err}`)
        setMicState('needs-download')
        return
      }
    }

    if (micState === 'idle' || micState === 'downloading' || micState === 'loading') {
      // Guard: only start when actually idle. If the model is loaded
      // but not yet warmed up (loading state), kick ensureReady via
      // whisper.prepare and fall through to record once it returns
      // ready. This replaces the previous silent-return when ready=false.
      let current = await bridge.call('whisper.isAvailable')
      if (!current.ready) {
        setMicState('loading')
        try {
          const r = await bridge.call('whisper.prepare')
          if (!r.ready) {
            alert(`Voice setup failed${r.error ? ': ' + r.error : ''}`)
            await refreshMic()
            return
          }
        } catch (err) {
          alert(`Voice setup failed: ${err instanceof Error ? err.message : err}`)
          await refreshMic()
          return
        }
        current = await bridge.call('whisper.isAvailable')
        if (!current.ready) {
          await refreshMic()
          return
        }
      }
      setMicState('recording')
      try {
        await bridge.call('whisper.startRecording')
      } catch {
        setMicState('idle')
      }
      return
    }

    if (micState === 'recording') {
      setMicState('transcribing')
      try {
        const { transcript } = await bridge.call('whisper.stopRecording')
        const trimmed = transcript.trim()
        if (trimmed) {
          // In a voice session auto-send is not a preference: the phone is on
          // the table and there is no one to tap Send.
          const autoSend =
            voiceActive || localStorage.getItem('whisperAutoSend') !== 'false'
          if (autoSend) {
            // `spoken` marks this as a VOICE turn, which is what makes the
            // server apply the preamble and the agent answer aloud. Only the
            // mic path sets it — a message typed during a voice session is
            // still a typed message, and claiming otherwise would put words in
            // someone's mouth in the transcript's provenance.
            const delivered = sendRelayInput(
              agentInstanceID,
              trimmed,
              undefined,
              undefined,
              voiceActive,
            )
            if (!delivered) {
              // Don't lose the transcription — drop it back in the composer
              // and flag the failure so the user can resend.
              setText(text ? `${text} ${trimmed}` : trimmed)
              setSendError(true)
            }
          } else {
            setText(text ? `${text} ${trimmed}` : trimmed)
          }
        }
      } finally {
        setMicState('idle')
      }
    }
  }

  const hasText = text.trim().length > 0
  const micDisabled = micState === 'downloading' || micState === 'transcribing'

  return (
    <div className="inputbar">
      {/* Hidden file input — triggered by the attach button */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => void handleFileSelected(e)}
      />
      <div className="inputbar__divider" />
      {mentionState && filteredMembers.length > 0 && (
        <div className="inputbar__mention-pop" role="listbox">
          {filteredMembers.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={i === mentionHighlight}
              className={`inputbar__mention-item${
                i === mentionHighlight ? ' inputbar__mention-item--active' : ''
              }`}
              // onMouseDown so the textarea doesn't lose focus before the
              // pick handler runs.
              onMouseDown={(e) => {
                e.preventDefault()
                pickMention(m)
              }}
            >
              <span className="inputbar__mention-name">{m.name}</span>
              {m.email && m.email !== m.name && (
                <span className="inputbar__mention-email">{m.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {attachError && (
        <div className="inputbar__attach-error">{attachError}</div>
      )}
      {sendError && (
        <div className="inputbar__attach-error" role="alert">
          Couldn’t reach the server — your message wasn’t sent. Check your
          connection and try again.
        </div>
      )}
      <div className="inputbar__row">
        <button
          className="inputbar__attach"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || micState === 'recording'}
          aria-label="Attach file"
        >
          {uploading ? '…' : '📎'}
        </button>
        <textarea
          ref={textareaRef}
          placeholder={`Send to ${agentName ?? 'the agent'}…`}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            recomputeMentionState(e.target.value, e.target.selectionStart ?? 0)
          }}
          onKeyUp={(e) => {
            // Track caret moves from arrow keys / clicks even when the
            // value didn't change.
            if (
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              e.key === 'ArrowUp' ||
              e.key === 'ArrowDown'
            ) {
              const el = e.currentTarget
              recomputeMentionState(el.value, el.selectionStart ?? 0)
            }
          }}
          onClick={(e) => {
            const el = e.currentTarget
            recomputeMentionState(el.value, el.selectionStart ?? 0)
          }}
          onBlur={() => {
            // Delay so a click on a popover item registers first.
            setTimeout(() => setMentionState(null), 100)
          }}
          onKeyDown={(e) => {
            if (mentionState && filteredMembers.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setMentionHighlight((i) => (i + 1) % filteredMembers.length)
                return
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault()
                setMentionHighlight(
                  (i) => (i - 1 + filteredMembers.length) % filteredMembers.length,
                )
                return
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                pickMention(filteredMembers[mentionHighlight])
                return
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                setMentionState(null)
                return
              }
            }
            if (e.key === 'Enter') {
              if (!bridge.isNative && !e.shiftKey) {
                // Browser: plain Enter sends; Shift+Enter falls through for newline.
                e.preventDefault()
                send()
              } else if (e.metaKey) {
                // iOS/Android: Cmd+Enter sends.
                e.preventDefault()
                send()
              }
            }
          }}
          className="inputbar__textarea"
          rows={1}
        />
        {micState !== 'hidden' && (
          <button
            className={`inputbar__mic${micState === 'recording' ? ' inputbar__mic--recording' : ''}`}
            onClick={onMic}
            disabled={micDisabled}
            aria-label={micButtonLabel(micState)}
          >
            {micButtonGlyph(micState)}
          </button>
        )}
        <button
          className={`inputbar__send${hasText ? ' inputbar__send--active' : ''}`}
          onClick={send}
          disabled={!hasText || uploading}
          aria-label="Send"
        >
          ↑
        </button>
      </div>
    </div>
  )
}

function micButtonGlyph(state: MicState): string {
  switch (state) {
    case 'downloading':
    case 'loading':
    case 'transcribing':
      return '…'
    case 'needs-download':
      return '⬇'
    default:
      return '🎙'
  }
}

function micButtonLabel(state: MicState): string {
  switch (state) {
    case 'recording':
      return 'Stop recording'
    case 'needs-download':
      return 'Download voice model'
    case 'downloading':
      return 'Downloading voice model'
    case 'loading':
      return 'Loading voice model'
    case 'transcribing':
      return 'Transcribing'
    default:
      return 'Record voice'
  }
}
